import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PhotoAnomaly } from '../types';
import { generateId } from '../utils/idGenerator';
import { useAnomalyHistoryStore } from './anomalyHistoryStore';

interface AnomalyStore {
  // Data
  anomalies: PhotoAnomaly[];

  // Actions
  addAnomaly: (anomaly: Omit<PhotoAnomaly, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateAnomaly: (id: string, updates: Partial<PhotoAnomaly>) => void;
  removeAnomaly: (id: string) => void;
  clearAnomalies: () => void;
  
  // Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Internal actions for undo/redo (don't record to history)
  _addAnomalyDirect: (anomaly: PhotoAnomaly) => void;
  _removeAnomalyDirect: (id: string) => void;
  _updateAnomalyDirect: (id: string, anomaly: PhotoAnomaly) => void;

  // Selectors
  getAnomaliesByElement: (elementId: number) => PhotoAnomaly[];
  getAnomaliesByPhoto: (photoFileName: string) => PhotoAnomaly[];
  getAnomaliesCount: () => number;
  getAnomalyById: (id: string) => PhotoAnomaly | undefined;
}

export const useAnomalyStore = create<AnomalyStore>()(
  persist(
    (set, get) => ({
      anomalies: [],

      addAnomaly: (anomalyData) => {
        const id = generateId();
        const now = new Date();
        const anomaly: PhotoAnomaly = {
          ...anomalyData,
          id,
          createdAt: now,
          updatedAt: now,
        };
        set((state) => ({ anomalies: [...state.anomalies, anomaly] }));
        
        // Record to history
        useAnomalyHistoryStore.getState().recordAdd(anomaly);
        
        return id;
      },

      updateAnomaly: (id, updates) => {
        const before = get().anomalies.find(a => a.id === id);
        
        set((state) => ({
          anomalies: state.anomalies.map((a) =>
            a.id === id ? { ...a, ...updates, updatedAt: new Date() } : a
          ),
        }));
        
        // Record to history
        if (before) {
          const after = get().anomalies.find(a => a.id === id);
          if (after) {
            useAnomalyHistoryStore.getState().recordUpdate(before, after);
          }
        }
      },

      removeAnomaly: (id) => {
        const anomaly = get().anomalies.find(a => a.id === id);
        
        set((state) => ({
          anomalies: state.anomalies.filter((a) => a.id !== id),
        }));
        
        // Record to history
        if (anomaly) {
          useAnomalyHistoryStore.getState().recordRemove(anomaly);
        }
      },

      clearAnomalies: () => {
        useAnomalyHistoryStore.getState().clearHistory();
        set({ anomalies: [] });
      },

      // Internal methods for undo/redo (don't record to history)
      _addAnomalyDirect: (anomaly) => {
        set((state) => ({ anomalies: [...state.anomalies, anomaly] }));
      },

      _removeAnomalyDirect: (id) => {
        set((state) => ({
          anomalies: state.anomalies.filter((a) => a.id !== id),
        }));
      },

      _updateAnomalyDirect: (id, anomaly) => {
        set((state) => ({
          anomalies: state.anomalies.map((a) => a.id === id ? anomaly : a),
        }));
      },

      undo: () => {
        const historyStore = useAnomalyHistoryStore.getState();
        const { _addAnomalyDirect, _removeAnomalyDirect, _updateAnomalyDirect } = get();
        
        historyStore.undo((entry) => {
          switch (entry.type) {
            case 'add':
              // Undo add = remove the anomaly
              if (entry.anomalyId) {
                _removeAnomalyDirect(entry.anomalyId);
              }
              break;
            case 'remove':
              // Undo remove = add the anomaly back
              if (entry.before) {
                _addAnomalyDirect(entry.before);
              }
              break;
            case 'update':
              // Undo update = restore previous state
              if (entry.before) {
                _updateAnomalyDirect(entry.anomalyId, entry.before);
              }
              break;
          }
        });
      },

      redo: () => {
        const historyStore = useAnomalyHistoryStore.getState();
        const { _addAnomalyDirect, _removeAnomalyDirect, _updateAnomalyDirect } = get();
        
        historyStore.redo((entry) => {
          switch (entry.type) {
            case 'add':
              // Redo add = add the anomaly again
              if (entry.after) {
                _addAnomalyDirect(entry.after);
              }
              break;
            case 'remove':
              // Redo remove = remove the anomaly again
              if (entry.anomalyId) {
                _removeAnomalyDirect(entry.anomalyId);
              }
              break;
            case 'update':
              // Redo update = apply the update again
              if (entry.after) {
                _updateAnomalyDirect(entry.anomalyId, entry.after);
              }
              break;
          }
        });
      },

      canUndo: () => useAnomalyHistoryStore.getState().canUndo(),
      canRedo: () => useAnomalyHistoryStore.getState().canRedo(),

      getAnomaliesByElement: (elementId) =>
        get().anomalies.filter((a) => a.elementId === elementId),

      getAnomaliesByPhoto: (photoFileName) =>
        get().anomalies.filter((a) => a.photoFileName === photoFileName),

      getAnomaliesCount: () => get().anomalies.length,

      getAnomalyById: (id) => get().anomalies.find(a => a.id === id),
    }),
    {
      name: 'elifly-anomalies',
      // Serialize dates properly
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const data = JSON.parse(str);
          if (data.state?.anomalies) {
            data.state.anomalies = data.state.anomalies.map((a: PhotoAnomaly) => ({
              ...a,
              createdAt: new Date(a.createdAt),
              updatedAt: new Date(a.updatedAt),
            }));
          }
          return data;
        },
        setItem: (name, value) => localStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
